import * as HeadlessContextMenu from '@radix-ui/react-context-menu'
import { motion } from 'framer-motion'
import React, { createRef } from 'react'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import { Checkbox } from '../../Checkbox'
import { Radio } from '../../Radio'
import { handleClick, handleKeyDown, handleSelect } from '../eventHandlers'
import { entryAnimation, initialAnimationState } from '../menuAnimationConfig'
import * as styles from '../styles'
import { RadioGroupProps } from '../types'

export {
  Root,
  Sub,
  SubTrigger,
  Trigger,
  ItemIndicator,
} from '@radix-ui/react-context-menu'

export const Portal: React.FC<
  React.ComponentProps<typeof HeadlessContextMenu.Portal>
> = ({ children, ...props }) => {
  const theme = useTheme()

  /**
   * HeadlessContextMenu.Portal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <HeadlessContextMenu.Portal {...props}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </HeadlessContextMenu.Portal>
  )
}
Portal.displayName = 'Lapidary: Portal ← ContextMenu'

export const Content = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.Content>,
  Omit<
    React.ComponentPropsWithoutRef<typeof HeadlessContextMenu.Content>,
    'asChild'
  > & { zIndex?: number }
>((props, forwardedRef) => {
  const theme = useTheme()

  const { children, zIndex, ...otherProps } = props
  return (
    <HeadlessContextMenu.Content
      {...otherProps}
      css={styles.list({ theme })}
      ref={forwardedRef}
      asChild
    >
      <motion.div
        initial={initialAnimationState}
        animate={entryAnimation}
        style={{
          transformOrigin: 'var(--radix-context-menu-content-transform-origin)',
          ...(zIndex == undefined ? {} : { zIndex }),
        }}
      >
        {children}
      </motion.div>
    </HeadlessContextMenu.Content>
  )
})
Content.displayName = 'Lapidary: Content ← ContextMenu'

export const Item = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.Item>,
  { destructive?: boolean } & React.ComponentPropsWithoutRef<
    typeof HeadlessContextMenu.Item
  >
>(({ destructive, ...props }, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessContextMenu.Item
      {...props}
      css={styles.item({ theme, destructive })}
      ref={forwardedRef}
    />
  )
})
Item.displayName = 'Lapidary: Item ← ContextMenu'

export const SubContent = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.SubContent>,
  React.ComponentPropsWithoutRef<typeof HeadlessContextMenu.SubContent>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessContextMenu.SubContent
      {...props}
      css={styles.list({ theme })}
      ref={forwardedRef}
    />
  )
})
SubContent.displayName = 'Lapidary: SubContent ← ContextMenu'

export const Separator = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.Separator>,
  React.ComponentPropsWithoutRef<typeof HeadlessContextMenu.Separator>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessContextMenu.Separator
      {...props}
      css={styles.separator({ theme })}
      ref={forwardedRef}
    />
  )
})
Separator.displayName = 'Lapidary: Separator ← ContextMenu'

export const Label = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.Label>,
  React.ComponentPropsWithoutRef<typeof HeadlessContextMenu.Label>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessContextMenu.Label
      {...props}
      css={styles.label({ theme })}
      ref={forwardedRef}
    />
  )
})
Label.displayName = 'Lapidary: Label ← ContextMenu'

export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.RadioGroup>,
  React.ComponentPropsWithoutRef<typeof HeadlessContextMenu.RadioGroup> &
    RadioGroupProps
>((props, forwardedRef) => {
  const theme = useTheme()
  const { name, useChildren = false, children, ...headlessProps } = props

  return (
    <HeadlessContextMenu.RadioGroup {...headlessProps} ref={forwardedRef}>
      {useChildren ? (
        children
      ) : (
        <Radio.Group defaultValue={props.value} name={name} theme={theme}>
          {children}
        </Radio.Group>
      )}
    </HeadlessContextMenu.RadioGroup>
  )
})
RadioGroup.displayName = 'Lapidary: RadioGroup ← ContextMenu'

export const RadioItem = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.RadioItem>,
  React.ComponentPropsWithoutRef<typeof HeadlessContextMenu.RadioItem> & {
    /**
     * When true renders children passed to this component in place of
     * Lapidary Radio Input elements. Standard radio button implementations
     * should not use this.
     * */
    useChildren?: boolean
  }
>(({ children, useChildren = false, ...radixProps }, forwardedRef) => {
  const theme = useTheme()
  const inputRef = createRef<HTMLInputElement>()

  return (
    <HeadlessContextMenu.RadioItem
      {...radixProps}
      css={styles.item({ theme })}
      onKeyDown={handleKeyDown(inputRef, radixProps.onKeyDown)}
      onSelect={handleSelect(radixProps.onSelect)}
      onClick={handleClick(inputRef, radixProps.onClick)}
      ref={forwardedRef}
    >
      {useChildren ? (
        children
      ) : (
        <Radio.Input
          label={children as string}
          ref={inputRef}
          value={radixProps.value}
        />
      )}
    </HeadlessContextMenu.RadioItem>
  )
})
RadioItem.displayName = 'Lapidary: RadioItem ← ContextMenu'

export const CheckboxItem = React.forwardRef<
  React.ElementRef<typeof HeadlessContextMenu.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof HeadlessContextMenu.CheckboxItem>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessContextMenu.CheckboxItem
      {...props}
      css={styles.item({ theme })}
      onSelect={handleSelect(props.onSelect)}
      ref={forwardedRef}
    >
      <Checkbox
        checked={props.checked as boolean}
        onChange={() => null}
        css={styles.checkboxOverrides}
      />
      {props.children}
    </HeadlessContextMenu.CheckboxItem>
  )
})
CheckboxItem.displayName = 'Lapidary: CheckboxItem ← ContextMenu'
